import { useEffect, useRef, useState } from 'react';
import { LoaderIcon } from 'lucide-react';
import { useStreamingStatus } from '@/client/lib/streamingStatusContext';
import { useThread, useThreadList } from '@assistant-ui/react';

const DEFAULT_LABELS = ['Thinking', 'Reflecting', 'Reasoning', 'Synthesizing', 'Resolving'];

function useRotatingLabel(active: boolean, intervalMs = 3000): string {
  const [index, setIndex] = useState(() => Math.floor(Math.random() * DEFAULT_LABELS.length));
  const timerRef = useRef<ReturnType<typeof setInterval>>();

  useEffect(() => {
    if (!active) return;
    // Pick a fresh random index when activation starts
    setIndex(Math.floor(Math.random() * DEFAULT_LABELS.length));
    timerRef.current = setInterval(() => {
      setIndex((prev) => {
        let next: number;
        do { next = Math.floor(Math.random() * DEFAULT_LABELS.length); } while (next === prev);
        return next;
      });
    }, intervalMs);
    return () => clearInterval(timerRef.current);
  }, [active, intervalMs]);

  return DEFAULT_LABELS[index];
}

export function StreamingStatus({ onStop }: { onStop?: () => void }) {
  const isRunning = useThread((s) => s.isRunning);
  const { getStatus, isThreadStreamActive, isJoinedThread } = useStreamingStatus();
  const mainThreadId = useThreadList((s) => s.mainThreadId);
  const threadItems = useThreadList((s) => s.threadItems);
  const currentThread = threadItems[mainThreadId];
  const currentThreadId = currentThread?.externalId ?? currentThread?.remoteId;
  const status = getStatus(currentThreadId);
  const joined = isJoinedThread(currentThreadId);

  // Per-thread stream tracking stays true during double-texting transitions
  // even when isRunning briefly gaps, preventing the widget from flickering.
  const visible = isRunning || !!status || isThreadStreamActive(currentThreadId);
  const defaultLabel = useRotatingLabel(visible && !status && !joined);

  return (
    <div
      className="grid transition-all duration-300 ease-in-out"
      style={{ gridTemplateRows: visible ? '1fr' : '0fr', opacity: visible ? 1 : 0 }}
    >
      <div className="overflow-hidden">
        <div className="flex w-full items-center gap-2 rounded-lg border border-border/60 bg-background/95 px-3 py-2 text-muted-foreground text-xs">
          <LoaderIcon className="size-3 animate-spin shrink-0" />
          <span className="flex-1 truncate shimmer shimmer-speed-80">
            {status || defaultLabel}
          </span>
          {onStop && !joined && (
            <button
              type="button"
              onClick={onStop}
              className="shrink-0 cursor-pointer rounded-sm bg-[hsl(262_60%_55%/0.08)] px-1.5 py-0.5 text-xs font-medium text-muted-foreground transition-colors hover:bg-[hsl(262_60%_55%/0.15)] hover:text-[hsl(262_60%_55%)]"
              aria-label="Stop response"
            >
              Stop
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
